import * as api from 'dragonmouse-shared/api/forge';
import { ChatMode, ChatStatus, DsChat } from 'dragonmouse-shared/interfaces';
import Icon, { IconType } from 'components/Icon';
import Pill from 'components/Widget/Pill';
import { Tooltip } from 'components/Widget/Tooltip';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { addDsBreakageNotification, clearDsBreakageNotifications } from 'redux/mode/actions';
import type { DsBreakageNotification } from 'redux/mode/reducers';
import forge from 'apis/forge';
import { useState } from 'react';
import * as S from './styles';

interface BreakageNotificationProps {
	dsChat: DsChat;
}

// groups impacts by the removed symbol (target), returns map of target -> source list
function groupByTarget(impacts: DsBreakageNotification['impacts']): Record<string, string[]> {
	const grouped: Record<string, string[]> = {};
	for (const { source, target } of impacts) {
		if (!grouped[target]) grouped[target] = [];
		if (!grouped[target].includes(source)) grouped[target].push(source);
	}
	return grouped;
}

const BreakageNotification = ({ dsChat }: BreakageNotificationProps) => {
	const dispatch = useDispatch();
	const [autofixTooltipId] = useState(() => `autofix-tip-${Math.random()}`);

	const { allNotifications } = useSelector(
		(r: RootReducer) => ({
			allNotifications: r.mode.dsBreakageNotifications,
		}),
		shallowEqual
	);

	const notifications = allNotifications.filter((n) => n.designSystemId === dsChat.designSystemId);

	const isChatProcessing =
		dsChat?.metadata?.status === ChatStatus.PROCESSING ||
		dsChat?.metadata?.status === ChatStatus.STOPPABLE;

	if (notifications.length === 0) return null;

	const allImpacts = notifications.flatMap((n) => n.impacts);
	const grouped = groupByTarget(allImpacts);

	const displayNames: Record<string, string> = {};
	for (const n of notifications) {
		Object.assign(displayNames, n.displayNames);
	}
	const dn = (sym: string) => displayNames[sym] ?? sym;

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------
	const onAutofix = async () => {
		if (!dsChat?.id || isChatProcessing) return;

		const lines: string[] = [
			'I made changes that removed some symbols. Please update the affected files:',
		];
		for (const [target, sources] of Object.entries(grouped)) {
			const sourceList = sources.map((s) => `\`${s}\``).join(', ');
			lines.push(`- \`${target}\` was removed — referenced by: ${sourceList}`);
		}
		const message = lines.join('\n');

		forge
			.updateDsChat({
				update: {
					id: dsChat.id,
					metadata: { status: ChatStatus.PROCESSING, statusMessage: '' },
				},
			})
			.catch((e) => console.error('Failed to update chat status:', e));

		const entry: api.AddUserEntryDsChatRequest['entry'] = {
			kind: 'user',
			message,
			attachments: [],
		};
		await forge.dsChatAddUserEntry.withToast()({
			dsChatId: dsChat.id,
			entry,
			chatMode: ChatMode.DESIGN_AND_CHAT,
		});

		dispatch(clearDsBreakageNotifications());
	};

	const onUndo = async () => {
		const seen = new Map<string, { path: string; content: string }>();
		for (const f of notifications.flatMap((n) => n.undoFiles)) {
			if (!seen.has(f.path)) seen.set(f.path, f);
		}
		const allUndoFiles = [...seen.values()];
		if (allUndoFiles.length === 0) return;
		const saved = [...notifications];
		dispatch(clearDsBreakageNotifications());
		try {
			await forge.updateDsFiles({
				designSystemId: dsChat.designSystemId,
				files: allUndoFiles,
			});
		} catch (err) {
			console.error('Failed to undo deletion:', err);
			for (const n of saved) dispatch(addDsBreakageNotification(n));
		}
	};

	const onDismiss = () => {
		dispatch(clearDsBreakageNotifications());
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------
	const renderImpactRow = (target: string, sources: string[]) => {
		const tooltipId = `impact-${target}`;
		const pillList = sources
			.map(
				(s) =>
					`<span style="display:inline-flex;align-items:center;padding:1px 8px;border-radius:12px;font-size:12px;background:#3C3C3C;color:#D8D8D8;margin:2px;margin-bottom:8px;white-space:nowrap">${dn(
						s
					)}</span>`
			)
			.join('');
		const tooltipHtml = `<div style="max-width:300px;padding:4px"><div style="font-size:12px;font-weight:600;color:#FFF;margin-bottom:12px;margin-left:4px">Used by</div><div style="display:flex;flex-wrap:wrap;gap:2px">${pillList}</div></div>`;

		return (
			<S.ImpactRow key={target}>
				<Pill label={dn(target)} />
				<S.ImpactText>→</S.ImpactText>
				<S.CountLink data-tooltip-id={tooltipId} data-tooltip-html={tooltipHtml}>
					{sources.length} {sources.length === 1 ? 'item' : 'items'}
				</S.CountLink>
				<Tooltip id={tooltipId} placement="top" />
			</S.ImpactRow>
		);
	};

	return (
		<S.Base>
			<S.Header>
				<S.Title>Still in use</S.Title>
				<S.CloseButton onClick={onDismiss} aria-label="Dismiss">
					<Icon type={IconType.CLOSE} width={10} height={10} />
				</S.CloseButton>
			</S.Header>
			<S.ImpactRows>
				{Object.entries(grouped).map(([target, sources]) => renderImpactRow(target, sources))}
			</S.ImpactRows>
			<S.Actions>
				<S.UndoButton onClick={onUndo}>Undo</S.UndoButton>
				<S.AutofixButton
					onClick={onAutofix}
					disabled={isChatProcessing}
					data-tooltip-id={autofixTooltipId}
					data-tooltip-content="Ask Moonchild to fix items that depend on what you deleted."
				>
					Autofix
				</S.AutofixButton>
				<Tooltip id={autofixTooltipId} placement="top" />
			</S.Actions>
		</S.Base>
	);
};

export default BreakageNotification;
